import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  createFilterOptions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { api } from '../services/api';
import payeeCacheService from '../services/payeeCache';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useToast } from '../components/ToastNotification';
import { useIsOwner } from '../components/RoleGate';
import { CenteredLoader } from '../components/LoadingIndicator';
import { PageHero, SectionContainer, Row } from '../components/ds';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { entryTypeLabelKey, interpolate, isEntryType } from '../utils/entryType';
import { LEDGER_GROUPS } from '../utils/ledgerGroups';

const errMessage = (err) =>
  err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

const GROUP_ORDER = LEDGER_GROUPS.map((g) => g.type);
// A workspace can hold thousands of payees; the picker lists the first
// matches only, as the Ledger page's picker does.
const filterPayees = createFilterOptions({ limit: 50 });

/**
 * Workspace tools > Ledger payees (#1944, epic #1936 decision 8).
 *
 * One row per ledger, archived last: the payee linked to it, or none. Entries
 * on a ledger's payee always save as Transfer, so this is where a workspace
 * makes its transfers structural. New ledgers link themselves at create;
 * this page links the ones made before that, and moves a link to another
 * payee. A link is never removed.
 *
 * Owner only, like renaming a ledger: the API (`PUT /ledgers/{id}` with
 * `linkPayeeId` or `createLinkedPayee`) refuses anyone else, and the page
 * shows the rows without their buttons.
 *
 * Picking a payee always confirms first, naming what the link does: the
 * payee takes the ledger's name, a non-Transfer default stops applying, and a
 * replaced payee is deleted (past lines keep its name).
 */
const LedgerPayees = ({ isAuthReady = false }) => {
  const { activeGroupId, loading: groupsLoading } = useGroup();
  const isOwner = useIsOwner();
  const { showError, showSuccess } = useToast();
  const t = useEngineStrings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [ledgers, setLedgers] = useState(null);
  const [payees, setPayees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The pick dialog: { ledger, payee }; payee is null until one is chosen.
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeGroupId) return;
    setLoading(true);
    setError(null);
    try {
      const [ledgersRes, payeesRes] = await Promise.all([
        api.get(activeGroupId, '/ledgers'),
        api.get(activeGroupId, '/payees')
      ]);
      setLedgers(ledgersRes?.ledgers || []);
      setPayees(payeesRes?.payees || []);
    } catch (err) {
      setError(errMessage(err));
      setLedgers([]);
    } finally {
      setLoading(false);
    }
  }, [activeGroupId]);

  useEffect(() => {
    if (isAuthReady && activeGroupId) load();
  }, [isAuthReady, activeGroupId, load]);

  const rows = useMemo(() => (ledgers || []).slice().sort((a, b) =>
    (a.archived ? 1 : 0) - (b.archived ? 1 : 0)
    || GROUP_ORDER.indexOf(a.type) - GROUP_ORDER.indexOf(b.type)
    || (a.name || '').localeCompare(b.name || '')
  ), [ledgers]);
  const payeeById = useMemo(() => new Map(payees.map((p) => [p.payeeId, p])), [payees]);
  const unlinked = useMemo(() => payees.filter((p) => !p.linkedLedgerId), [payees]);
  const sameName = (ledger) => unlinked.find((p) => p.lowercaseName === ledger.lowercaseName) || null;

  const save = async (ledger, body, done) => {
    setSaving(true);
    try {
      await api.put(activeGroupId, `/ledgers/${encodeURIComponent(ledger.ledgerId)}`, { ...body, version: ledger.version });
      await payeeCacheService.invalidatePayeeCache('ledger_payee_linked');
      showSuccess(done);
      setDialog(null);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const create = (ledger) => save(ledger, { createLinkedPayee: true }, interpolate(t['ledgerPayees.linkedTo'], { payee: ledger.name }));
  const confirmLink = () => save(
    dialog.ledger,
    { linkPayeeId: dialog.payee.payeeId },
    interpolate(t['ledgerPayees.linkedTo'], { payee: dialog.ledger.name })
  );

  // What linking `payee` to `ledger` does, one sentence each.
  const consequences = (ledger, payee) => {
    const lines = [];
    if (payee.lowercaseName !== ledger.lowercaseName) {
      lines.push(interpolate(t['ledgerPayees.confirmRename'], { payee: payee.name, ledger: ledger.name }));
    }
    if (isEntryType(payee.defaultEntryType) && payee.defaultEntryType !== 'transfer') {
      lines.push(interpolate(t['ledgerPayees.confirmDefault'], { payee: payee.name, type: t[entryTypeLabelKey(payee.defaultEntryType)] }));
    }
    const current = ledger.linkedPayeeId && payeeById.get(ledger.linkedPayeeId);
    if (current) lines.push(interpolate(t['ledgerPayees.confirmReplace'], { old: current.name }));
    return lines;
  };

  if (!isAuthReady || groupsLoading) return <CenteredLoader />;

  const actions = (ledger) => {
    if (!isOwner) return null;
    const button = (label, onClick, testId) => (
      <Button size="small" variant="text" onClick={onClick} disabled={saving} data-testid={testId} sx={{ whiteSpace: 'nowrap' }}>
        {label}
      </Button>
    );
    if (ledger.linkedPayeeId) {
      return button(t['ledgerPayees.change'], () => setDialog({ ledger, payee: null }), 'ledger-payee-change');
    }
    const same = sameName(ledger);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '4px' }}>
        {same
          ? button(interpolate(t['ledgerPayees.link'], { name: same.name }), () => setDialog({ ledger, payee: same }), 'ledger-payee-link-same')
          : button(interpolate(t['ledgerPayees.create'], { name: ledger.name }), () => create(ledger), 'ledger-payee-create')}
        {button(t['ledgerPayees.pick'], () => setDialog({ ledger, payee: null }), 'ledger-payee-pick')}
      </Box>
    );
  };

  return (
    <Box sx={{ pb: 8 }}>
      <PageHero label={t['ledgerPayees.title']} />

      <Box sx={{ mt: { xs: '12px', md: '14px' } }}>
        <Alert severity="info" sx={{ mb: 2 }}>{t['ledgerPayees.intro']}</Alert>
        {!isOwner && <Alert severity="warning" sx={{ mb: 2 }}>{t['ledgerPayees.ownerOnly']}</Alert>}

        {loading && <CenteredLoader />}
        {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && ledgers && (
          <SectionContainer
            title={t['ledgerPayees.title']}
            count={rows.length.toLocaleString('en-US')}
            category="neutral"
            density={isMobile ? 'compact' : 'default'}
          >
            {rows.length === 0 ? (
              <Box sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>{t['ledgerPayees.empty']}</Box>
            ) : rows.map((ledger, idx) => {
              const group = LEDGER_GROUPS.find((g) => g.type === ledger.type);
              const Icon = group?.Icon;
              const linked = ledger.linkedPayeeId && payeeById.get(ledger.linkedPayeeId);
              return (
                <Row
                  key={ledger.ledgerId}
                  data-testid="ledger-payee-row"
                  category={group?.category || 'neutral'}
                  icon={Icon ? <Icon /> : null}
                  name={ledger.name}
                  meta={[
                    linked ? interpolate(t['ledgerPayees.linkedTo'], { payee: linked.name }) : t['ledgerPayees.notLinked'],
                    ledger.archived ? t['ledgerPayees.archived'] : null
                  ].filter(Boolean).join(' · ')}
                  archived={Boolean(ledger.archived)}
                  trailing={actions(ledger)}
                  last={idx === rows.length - 1}
                />
              );
            })}
          </SectionContainer>
        )}
      </Box>

      <Dialog open={Boolean(dialog)} onClose={() => !saving && setDialog(null)} fullWidth maxWidth="xs">
        {dialog && (
          <>
            <DialogTitle>
              {dialog.payee
                ? interpolate(t['ledgerPayees.confirmTitle'], { payee: dialog.payee.name, ledger: dialog.ledger.name })
                : (dialog.ledger.linkedPayeeId ? t['ledgerPayees.change'] : t['ledgerPayees.pick'])}
            </DialogTitle>
            <DialogContent>
              <Autocomplete
                autoHighlight
                options={unlinked}
                filterOptions={filterPayees}
                value={dialog.payee}
                onChange={(_e, payee) => setDialog((prev) => ({ ...prev, payee }))}
                getOptionLabel={(p) => p.name || ''}
                isOptionEqualToValue={(a, b) => a.payeeId === b.payeeId}
                renderInput={(params) => (
                  <TextField {...params} size="small" autoFocus label={t['ledgerPayees.pick']} sx={{ mt: 1 }} />
                )}
              />
              {dialog.payee && consequences(dialog.ledger, dialog.payee).map((line) => (
                <DialogContentText key={line} sx={{ mt: 1.5, fontSize: 14 }}>{line}</DialogContentText>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)} disabled={saving}>{t['ledgerPayees.cancel']}</Button>
              <Button variant="contained" onClick={confirmLink} disabled={saving || !dialog.payee}>
                {t['ledgerPayees.confirm']}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default LedgerPayees;
